import { cn } from '../../utils/cn';

export interface ScrollEdgesProps {
    /** Content has scrolled away above: soften the top edge. */
    showTop: boolean;
    /** Content continues below: soften the bottom edge. */
    showBottom: boolean;
    /**
     * Lifts the bottom fade off the pane's edge, for a step that pins its own
     * action there — the same offset the scroll hint takes, so the fade ends
     * where that bar begins instead of washing over it.
     */
    bottomClassName?: string;
    /**
     * Measured distance of a pinned bar above the pane's bottom edge, in
     * pixels. Wins over `bottomClassName`, so the fade ends exactly on the bar.
     */
    bottomOffset?: number | null;
}

/**
 * Soft white edges where the content runs past the pane. A hard crop cuts a
 * field in half at the fold; fading it out says "this continues" without a
 * scrollbar in the way. Each edge only shows when there is something beyond it,
 * so a step that fits sits clean.
 */
export const ScrollEdges = ({
    showTop,
    showBottom,
    bottomClassName,
    bottomOffset,
}: ScrollEdgesProps) => (
    <>
        <div
            aria-hidden
            className={cn(
                'pointer-events-none absolute inset-x-0 top-0 z-[1] h-10 bg-linear-to-b from-white via-white/70 to-white/0 transition-opacity duration-300 ease-out',
                showTop ? 'opacity-100' : 'opacity-0',
            )}
        />
        <div
            aria-hidden
            className={cn(
                'pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-14 bg-linear-to-t from-white via-white/70 to-white/0 transition-opacity duration-300 ease-out',
                showBottom ? 'opacity-100' : 'opacity-0',
                bottomOffset == null && bottomClassName,
            )}
            style={bottomOffset == null ? undefined : { bottom: bottomOffset }}
        />
    </>
);
